import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { api, imgSrc } from "@/lib/api";

export const CategoryMenu = () => {
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        api.get("/products")
            .then((res) => {
                const map = {};
                res.data.forEach((p) => {
                    if (!map[p.category]) map[p.category] = { name: p.category, count: 0, image: p.image };
                    map[p.category].count += 1;
                });
                setCategories(Object.values(map));
            })
            .catch(() => {});
    }, []);

    if (categories.length === 0) return null;

    const spanFor = (i) => {
        if (i === 0) return "lg:col-span-7";
        if (i === 1) return "lg:col-span-5 lg:mt-16";
        return "lg:col-span-4";
    };

    return (
        <section data-testid="category-menu-section" className="border-b border-line bg-[#EFEAE0]">
            <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
                <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay">Categories</p>
                        <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-gold md:text-4xl">
                            Find your <span className="italic text-espresso">carry.</span>
                        </h2>
                    </div>
                    <Link
                        to="/shop"
                        data-testid="category-view-all-link"
                        className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:text-gold"
                    >
                        View everything
                        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </div>
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-12">
                    {categories.map((cat, i) => (
                        <motion.div
                            key={cat.name}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                            className={spanFor(i)}
                        >
                            <Link to={`/shop?category=${encodeURIComponent(cat.name)}`} data-testid={`category-card-${cat.name.toLowerCase()}`} className="group block">
                                <div className="relative aspect-[4/5] overflow-hidden">
                                    <img
                                        src={imgSrc(cat.image)}
                                        alt={cat.name}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 via-transparent to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6">
                                        <div>
                                            <p className="font-serif text-3xl font-light tracking-tight text-sand">{cat.name}</p>
                                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-sand/70">
                                                {cat.count} piece{cat.count === 1 ? "" : "s"}
                                            </p>
                                        </div>
                                        <span className="flex h-11 w-11 items-center justify-center border border-sand/50 text-sand transition-all duration-300 group-hover:bg-gold group-hover:border-gold group-hover:text-espresso">
                                            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
